import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const OrderDetail = () => {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data: order } = useQuery({
    queryKey: ["order", id],
    queryFn: async () => {
      const { data } = await supabase
        .from("orders")
        .select("id, total_cents, currency, status, created_at, paid_at")
        .eq("id", id)
        .maybeSingle();
      return data;
    },
    enabled: !!id,
  });
  const { data: items } = useQuery({
    queryKey: ["order-items", id],
    queryFn: async () => {
      const { data } = await supabase
        .from("order_items")
        .select("id, qty, price_cents, product:products(title)")
        .eq("order_id", id);
      return data ?? [];
    },
    enabled: !!id,
  });

  const markPaid = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("Missing order id");
      const { error } = await supabase
        .from("orders")
        .update({ status: "paid", paid_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Order marked as paid");
      qc.invalidateQueries({ queryKey: ["order", id] });
    },
    onError: (e: any) => toast.error(e.message || "Failed to update order"),
  });
  if (!order) return (
    <main className="container mx-auto px-4 py-10">
      <h1 className="text-3xl font-heading font-bold mb-6">Order</h1>
      <p className="text-muted-foreground">Order not found or loading...</p>
    </main>
  );

  const total = items?.reduce((s, it) => s + it.qty * it.price_cents, 0) ?? order.total_cents;

  return (
    <main className="container mx-auto px-4 py-10">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <h1 className="text-3xl font-heading font-bold">Order #{order.id.slice(0, 8)}</h1>
          <div className="text-muted-foreground">
            Status: {order.status}
            {order.paid_at ? ` · Paid ${new Date(order.paid_at).toLocaleString()}` : ` · Created ${new Date(order.created_at).toLocaleString()}`}
          </div>
        </div>
        {order.status !== "paid" && (
          <Button onClick={() => markPaid.mutate()} disabled={markPaid.isPending}>Mark as Paid</Button>
        )}
      </div>
      <div className="grid gap-3 mt-4">
        {(items ?? []).map((it) => (
          <div key={it.id} className="flex items-center justify-between border rounded-lg p-4">
            <div className="font-medium">{it.product?.title}</div>
            <div className="text-sm text-muted-foreground">x{it.qty}</div>
            <div className="font-semibold">{order.currency} {(it.price_cents/100).toFixed(2)}</div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between border-t pt-4 mt-4">
        <div className="text-xl font-semibold">Total</div>
        <div className="text-xl font-bold">{order.currency} {(total/100).toFixed(2)}</div>
      </div>
    </main>
  );
};
export default OrderDetail;